import styled from 'styled-components';
import { Input, Checkbox } from 'antd';
import Button from '../../UI/buttons/Button';
import { createGlobalStyle } from 'styled-components';
import { HirePortTheme } from '../../hooks/useHirePortTheme';

// Global style overrides to handle focus and hover states
export const GlobalAntOverrides = createGlobalStyle<{ theme: HirePortTheme }>`
  // Override all Ant Design blue focus/hover styles in this component
  .ant-input:focus,
  .ant-input-focused,
  .ant-select-focused .ant-select-selector,
  .ant-select-selector:focus,
  .ant-select-open .ant-select-selector,
  .ant-btn-primary:focus,
  .ant-dropdown:focus {
    border-color: ${({ theme }) => theme.primaryColor} !important;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.primaryColor}40 !important;
    outline: 0;
  }
  
  .ant-input:hover,
  .ant-select:hover .ant-select-selector,
  .ant-btn:hover:not(.ant-btn-primary):not(:disabled),
  .ant-btn:focus:not(.ant-btn-primary):not(:disabled) {
    border-color: ${({ theme }) => theme.hoverColor} !important;
  }
  
  .ant-btn-primary:not(:disabled) {
    background-color: ${({ theme }) => theme.primaryColor} !important;
    border-color: ${({ theme }) => theme.primaryColor} !important;

    &:hover {
      background-color: ${({ theme }) => theme.hoverColor} !important;
      border-color: ${({ theme }) => theme.hoverColor} !important;
    }
  }
  
  .ant-dropdown-menu-item {
    &:hover {
      background-color: ${({ theme }) => theme.lozengeColor} !important;
      font-weight: normal;
    }
  }

  // Disable hover effects on disabled elements
  .ant-btn:disabled,
  .ant-input:disabled,
  .ant-select-disabled {
    pointer-events: none;
  }
`;

// Layout components
export const PageWrapper = styled.section<{ theme: HirePortTheme }>`
  background: ${({ theme }) => theme.lozengeColor || '#f7f9fb'};
  width: 100%;
  display: flex;
  min-height: 100vh;
  flex-flow: row-reverse;
`;

export const FormWrapper = styled.div`
  background: #fff;
  width: 61%;
  padding: 4.5rem 4rem;
  display: flex;
  flex-direction: column;
  position: relative;

  @media (max-width: 1200px) {
    width: 70%;
  }
  @media (max-width: 768px) {
    width: 90%;
    padding: 3rem;
  }
`;

// New component for top controls row
export const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

// Search components
export const SearchWrapper = styled.div`
  display: flex;
  margin-bottom: 16px;
  align-items: center;
  
  .anticon {
    color: #aebeca;
  }
`;

export const SearchInput = styled(Input) <{ theme: HirePortTheme }>`
  width: 240px;
  border-radius: ${({ theme }) => theme.radiusSmall};
  background-color: white;
  border: 1px solid #d9d9d9;
  
  &.ant-input-affix-wrapper {
    padding: 4px 11px;
    
    .ant-input-prefix {
      margin-right: 8px;
      color: #aebeca;
    }
    
    .ant-input {
      background: transparent;
      font-size: 13px;
      
      &::placeholder {
        color: #aebeca;
      }
    }
  }

  // Add platform standard hover/focus styles
  &:hover {
    border-color: ${({ theme }) => theme.hoverColor} !important;
  }

  &:focus,
  &.ant-input-focused,
  &.ant-input-affix-wrapper-focused {
    border-color: ${({ theme }) => theme.primaryColor} !important;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.primaryColor}40 !important;
    outline: 0;
  }
  
  // Ensure the input has a smooth transition on hover/focus
  transition: all 0.2s;
`;

// Instructions box
export const InstructionsBox = styled.div<{ theme: HirePortTheme }>`
  margin-bottom: 16px;
  padding: 16px;
  background-color: ${({ theme }) => theme.lozengeColor || '#fafafa'};
  border: 1px solid #f0f0f0;
  border-radius: ${({ theme }) => theme.radius};
  font-size: 13px;
  color: #666;
`;

export const InstructionsTitle = styled.h3`
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
`;

export const InstructionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const InstructionItem = styled.div`
  display: flex;
  align-items: flex-start;
  color: #666;
`;

export const Bullet = styled.span`
  margin-right: 4px;
  color: #999;
`;

// Action bar
export const ButtonGroup = styled.div<{ theme: HirePortTheme }>`
  display: flex;
  gap: 8px;
  
  // Only apply hover/focus styles to enabled buttons
  .ant-btn:not(:disabled) {
    &:hover, &:focus {
      color: ${({ theme }) => theme.primaryColor} !important;
      border-color: ${({ theme }) => theme.primaryColor} !important;
      font-weight: normal;
    }
    
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.primaryColor}40 !important;
    }
  }

  // Remove any hover effects for disabled buttons
  .ant-btn:disabled {
    pointer-events: none;
  }
`;

export const ActionBar = styled.div<{ theme: HirePortTheme }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.lozengeColor || '#fafafa'};
  padding: 10px 16px;
  border-radius: ${({ theme }) => theme.radius};
  border: 1px solid #e8e8e8;
  margin-bottom: 16px;
  
  ${ButtonGroup} {
    .ant-btn {
        border-radius: ${({ theme }) => theme.radiusSmall};
    }
    .ant-dropdown-trigger.ant-btn {
        border-radius: ${({ theme }) => theme.radiusSmall};
    }
  }
`;

export const SelectionInfo = styled.div`
  font-size: 13px;
  color: #666;
`;

export const TypeButton = styled(Button) <{ $stageType?: string; theme: HirePortTheme }>`
  border-left: ${props => props.$stageType ? `3px solid ${props.$stageType}` : 'none'};
  padding-left: 8px;

  &.ant-btn {
    display: flex;
    align-items: center;
    transition: all 0.2s;
    
    &:not(:disabled) {
      &:hover, &:focus {
        border-left-color: ${props => props.$stageType ? `${props.$stageType}` : props.theme.hoverColor};
      }
    }
  }
`;

// Stage group components
export const GroupHeader = styled.div<{ theme: HirePortTheme }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  background-color: ${({ theme }) => theme.lozengeColor || '#fafafa'};
  border: 1px solid #f0f0f0;
  border-radius: ${({ theme }) => theme.radius};
  margin: 16px 0 8px 0;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => `${theme.lozengeColor}80`};
  }
`;

export const GroupTitle = styled.div`
  font-weight: 500;
  font-size: 14px;
  display: flex;
  align-items: center;
`;

export const GroupCount = styled.span`
  margin-left: 8px;
  color: #999;
  font-weight: normal;
`;

export const StagesGrid = styled.div<{ $isListLayout?: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$isListLayout ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))'};
  gap: 12px;
  margin-top: 8px;
  padding: 4px 0;
`;

// Specific Checkbox for StageCard to control checked style
export const StageCardCheckbox = styled(Checkbox) <{ theme: HirePortTheme }>`
  &.ant-checkbox-wrapper:not(.ant-checkbox-wrapper-disabled):hover .ant-checkbox-checked:not(.ant-checkbox-disabled) .ant-checkbox-inner,
  & .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #627d95 !important;
    border-color: #627d95 !important;
  }

  &.ant-checkbox-wrapper:not(.ant-checkbox-wrapper-disabled):hover .ant-checkbox-inner,
  & .ant-checkbox:hover .ant-checkbox-inner,
  & .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: ${({ theme }) => theme.primaryColor} !important;
  }
`;

// Stage card components
export const StageCard = styled.div<{ $stageType?: string, $isSelected: boolean, theme: HirePortTheme }>`
  display: flex;
  flex-direction: column;
  padding: 10px 12px;
  background: white;
  border-radius: ${({ theme }) => theme.radius};
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.03);
  border: 1px solid ${props => props.$isSelected ? props.theme.primaryColor : '#f0f0f0'};
  border-left: 3px solid ${props => props.$stageType ? props.$stageType : '#f0f0f0'};
  position: relative;
  cursor: pointer;
`;

export const StageCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`;

export const StageNameWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

export const StageName = styled.div`
  font-weight: 500;
  font-size: 12.5px;
  margin-left: 6px;
  color: #333;
  word-break: break-word;
  overflow-wrap: break-word;
  line-height: 1.4;
  flex: 1;
  min-width: 0;
`;

export const StageTypeLabel = styled.div`
  display: flex;
  align-items: center;
  color: #888;
  font-size: 11.5px;
`;

export const StageTypeIcon = styled.div<{ $stageType?: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 4px;
  background-color: ${props => props.$stageType || '#d9d9d9'};
`;

export const StageLabelText = styled.span`
  flex: 1;
`;

export const StageOrder = styled.span`
  font-size: 10.5px;
  color: #999;
`;

export const DragHandle = styled.div`
  cursor: grab;
  padding: 1px;
  color: #bbb;
  font-size: 14px;
  transition: color 0.2s;
  
  &:hover {
    color: #888;
  }
`;

export const DropZone = styled.div<{ theme: HirePortTheme }>`
  background-color: ${({ theme }) => theme.lozengeColor || '#fafafa'};
  border: 1px dashed #e8e8e8;
  border-radius: ${({ theme }) => theme.radius};
  padding: 16px;
  text-align: center;
  color: #888;
  font-size: 12px;
  width: 100%;
  margin: 6px 0;
  grid-column: 1 / -1;
`;

export const HelpText = styled.div`
  font-size: 11px;
  color: #999;
  margin-top: 4px;
  margin-left: 10px;
  font-style: italic;
`;

export const SelectionCorner = styled.div<{ theme: HirePortTheme }>`
  position: absolute;
  top: 0;
  right: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 12px 12px 0;
  border-color: transparent ${({ theme }) => theme.primaryColor} transparent transparent;
`;

export const ChangedIndicator = styled(Button) <{ theme: HirePortTheme }>`
  position: absolute;
  top: 8px;
  right: 8px;
  color: ${({ theme }) => theme.primaryColor};
  font-size: 12px;
`;

export const ButtonsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  gap: 8px;
`; 